import type {
  SkillPublishReply,
  SkillPublishRequest,
  SkillUploadApi,
} from '../../services/skillUploadApi';

export const MAX_SKILL_ZIP_BYTES = 64 * 1024 * 1024;
export type SkillUploadPhase =
  | 'hashing'
  | 'signing'
  | 'uploading'
  | 'publishing';
export interface SkillUploadInput {
  itemKey: string;
  version: string;
  file: File;
  displayName?: Record<string, string>;
  displayDescription?: Record<string, string>;
  agentProviders: string[];
}
const slug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const semver =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

export function validateSkillUpload(input: SkillUploadInput): void {
  if (
    !slug.test(input.itemKey) ||
    input.itemKey.length > 64 ||
    input.itemKey.startsWith('tutti-')
  ) {
    throw new Error(
      'Skill key 需为 1–64 位小写字母、数字或单连字符，不能以 tutti- 开头',
    );
  }
  if (!semver.test(input.version))
    throw new Error('版本需符合 SemVer，例如 1.0.0');
  if (
    !input.file.name.toLowerCase().endsWith('.zip') ||
    input.file.size === 0 ||
    input.file.size > MAX_SKILL_ZIP_BYTES
  ) {
    throw new Error('请选择非空 ZIP 文件，大小不超过 64 MiB');
  }
  if (
    input.agentProviders.length === 0 ||
    input.agentProviders.length > 16 ||
    input.agentProviders.some((p) => !slug.test(p)) ||
    new Set(input.agentProviders).size !== input.agentProviders.length
  ) {
    throw new Error('请选择支持的 Agent Provider');
  }
}
export async function hashSkillFile(file: File): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(hash), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
}
export function createSkillUploadFlow(
  api: SkillUploadApi,
  hashFile = hashSkillFile,
) {
  let generation = 0;
  let active: AbortController | undefined;
  const cancel = () => {
    generation += 1;
    active?.abort();
    active = undefined;
  };
  return {
    cancel,
    async run(
      input: SkillUploadInput,
      onPhase: (phase: SkillUploadPhase) => void,
    ): Promise<SkillPublishReply> {
      cancel();
      validateSkillUpload(input);
      const controller = new AbortController();
      active = controller;
      const current = generation;
      const assertCurrent = () => {
        if (controller.signal.aborted || current !== generation)
          throw new DOMException('上传已取消', 'AbortError');
      };
      const phase = (next: SkillUploadPhase) => {
        assertCurrent();
        onPhase(next);
      };
      // Snapshot request metadata before any async work or form edits.
      const metadata = {
        itemKey: input.itemKey,
        version: input.version,
        displayName: { ...input.displayName },
        displayDescription: { ...input.displayDescription },
        agentProviders: [...input.agentProviders],
        iconUrl: '',
        minimumHostVersion: '0.0.0',
      };
      try {
        phase('hashing');
        const artifactSha256 = await hashFile(input.file);
        assertCurrent();
        const identity = {
          itemKey: metadata.itemKey,
          version: metadata.version,
          artifactSha256,
          artifactSizeBytes: String(input.file.size),
        };
        phase('signing');
        const session = await api.createSession(identity, controller.signal);
        assertCurrent();
        phase('uploading');
        await api.putFile(session, input.file, controller.signal);
        assertCurrent();
        phase('publishing');
        const request: SkillPublishRequest = { ...metadata, ...identity };
        const reply = await api.publish(request, controller.signal);
        assertCurrent();
        if (
          !reply.release ||
          reply.release.itemType !== 'skill' ||
          reply.release.itemKey !== identity.itemKey ||
          reply.release.version !== identity.version ||
          reply.release.artifactSha256 !== identity.artifactSha256 ||
          String(reply.release.artifactSizeBytes) !==
            identity.artifactSizeBytes ||
          reply.release.status !== 'available'
        ) {
          throw new Error('发布返回的版本与上传内容不一致，请刷新后核实');
        }
        return reply;
      } finally {
        if (current === generation) active = undefined;
      }
    },
  };
}
